import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function WatchRunningFastPage() {
  usePageTracking('watch_running_fast_guide', {
    page_title: 'Why Is My Watch Running Fast or Slow? | ChronoLog'
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>Why Is My Watch Running Fast or Slow? | ChronoLog</title>
        <meta name="description" content="A watch that suddenly runs fast is usually magnetized — an easy fix. Gradual slowing means service is due. Here's how to diagnose which problem is yours." />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Why Is My Watch Running Fast or Slow?" />
        <meta property="og:description" content="Sudden gains usually mean magnetization. Gradual losses usually mean a service is due. How to tell which case is yours and what to do about it." />
        <meta property="og:url" content="https://www.chronolog.watch/watch-running-fast" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/watch-running-fast" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "Why Is My Watch Running Fast or Slow?",
                "description": "How to diagnose a mechanical watch that's gaining or losing time: magnetization, worn lubricants, position effects, and normal variation — and how to tell which case is yours.",
                "author": { "@type": "Organization", "name": "ChronoLog" },
                "publisher": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "url": "https://www.chronolog.watch/watch-running-fast",
                "datePublished": "2026-10-12",
                "keywords": "watch running fast, watch gaining time, magnetized watch, watch running slow, watch losing time, mechanical watch accuracy, demagnetize watch"
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Why did my watch suddenly start running fast?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "A sudden, large gain — anywhere from 30 seconds to several minutes per day — almost always means the movement has become magnetized. A magnetic field causes the coils of the hairspring to stick together, which effectively shortens the spring and makes the balance wheel oscillate faster. Common culprits are magnetic bag clasps, phone and tablet cases, laptop speakers, and headphones. The fix is demagnetization, which takes seconds and doesn't require opening the watch."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How do I know if my watch is magnetized?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "The classic symptom is a sudden jump to running fast, often by a minute or more per day. To confirm, hold the watch close to a compass — a magnetized watch will deflect the needle as you move it around. Free compass or magnetometer apps on a smartphone can show the same effect. A timegrapher reading that shows a dramatically positive rate on a watch that was previously fine points the same way."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can I demagnetize a watch at home?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. Hobbyist demagnetizers typically cost under $20 online. Place the watch on the unit, press the button, and slowly draw the watch away while the field is active — the alternating field scrambles the residual magnetism in the hairspring. If you'd rather not do it yourself, any watchmaker can demagnetize a watch in a couple of minutes, often for little or no charge. Demagnetizing doesn't open or harm the watch."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How much daily variation is normal for a mechanical watch?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Day-to-day swings of a few seconds are completely normal, because rate changes with position, winding state, and temperature. Many non-chronometer movements are considered within spec anywhere in the ±10 to ±20 seconds per day range, while COSC-certified chronometers must average −4 to +6 seconds per day. What matters is the pattern: a stable average with small scatter is healthy, while a sudden jump or a steady drift in one direction signals a real problem."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>Why Is My Watch Running Fast or Slow?</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          If your watch suddenly started gaining time — 30 seconds, a minute, even several minutes a day — the most likely cause is magnetization, and it's a cheap, two-minute fix. If it has been slowing down gradually over months or years, the movement's lubricants are probably worn and a service is due. If the rate just seems inconsistent — fine one day, off the next — the usual explanation is position: how the watch sits when you're not wearing it.
        </p>

        <p>
          Those three patterns cover most real-world cases. Here's how to match your symptom to its cause.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Symptom</th>
                <th>Most likely cause</th>
                <th>Fix</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Suddenly fast, often dramatically</td>
                <td>Magnetized hairspring</td>
                <td>Demagnetize (minutes, cheap)</td>
              </tr>
              <tr>
                <td>Gradually slower over months</td>
                <td>Degraded lubricants, low amplitude</td>
                <td>Service</td>
              </tr>
              <tr>
                <td>Inconsistent day to day</td>
                <td>Position and winding-state effects</td>
                <td>Usually nothing — or regulation</td>
              </tr>
              <tr>
                <td>Steadily a few seconds off, every day</td>
                <td>Normal rate for that movement</td>
                <td>Regulation if it bothers you</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Magnetized movement: the sudden-fast culprit</h2>

        <p>
          The hairspring is the heart of a mechanical watch's timekeeping: a coiled spring, often finer than a human hair, that controls how fast the balance wheel swings back and forth. When a watch passes through a strong enough magnetic field, the steel in that spring can pick up residual magnetism. The coils then attract each other and stick together as the spring breathes in and out.
        </p>

        <p>
          A hairspring with stuck coils behaves like a shorter spring, and a shorter spring oscillates faster. That's why magnetization almost always makes a watch run <em>fast</em> — and often absurdly fast. Gains of a minute or more per day are common, which is far beyond anything a healthy movement would do on its own. The suddenness is the tell: the watch was fine last week, and now it's gaining minutes.
        </p>

        <h3>Where the magnetism comes from</h3>

        <p>
          You don't need an MRI machine. Everyday objects are the usual suspects: magnetic clasps on bags and purses, phone and tablet cases with closure magnets, laptop lids and speakers, wireless charging pads, headphones, refrigerator magnets, and induction cooktops. Resting your wrist next to a laptop's palm rest for hours, or tossing the watch in a bag pocket next to a magnetic clasp, is all it takes.
        </p>

        <h3>How to confirm it</h3>

        <p>
          The simplest test costs nothing: hold the watch near a compass and move it around. If the needle deflects and follows the watch, the movement is magnetized. A free magnetometer or compass app on a smartphone shows the same thing — watch the reading jump as you bring the watch close. The other confirmation is the rate itself: a big, abrupt positive change with no other explanation is magnetism until proven otherwise.
        </p>

        <h3>Demagnetizing</h3>

        <p>
          This is the satisfying part. Hobbyist demagnetizers typically sell for under $20. You place the watch on the unit, press the button, and slowly pull the watch away while the field is active — the alternating field randomizes the magnetism in the spring, and the problem is gone. No opening the case, no tools, no risk to the movement when done as directed. Any watchmaker will also do it in a couple of minutes, often free or nearly so.
        </p>

        <p>
          Some modern watches are largely immune: movements with silicon hairsprings don't magnetize, and watches certified under METAS's Master Chronometer program must resist fields of 15,000 gauss. If you own one of these and it's running fast, look elsewhere for the cause.
        </p>

        <h2>Needs a service: the slow drift pattern</h2>

        <p>
          The opposite pattern — a watch that loses a little more time each month — usually means the movement's oils are past their useful life. Lubricants in a watch degrade over years: they thicken, dry out, or migrate away from the pivots they're supposed to protect. Friction rises, and the balance wheel swings through a smaller arc (lower amplitude). A low-amplitude balance keeps worse time, is more sensitive to position changes, and typically drifts slow.
        </p>

        <p>
          Manufacturers commonly recommend a service somewhere in the five-to-ten-year range, though intervals vary by brand and how the watch is used. The signature of a service-due watch is gradual decline: it used to run within a few seconds a day, and over months it has slipped to losing 15, then 20, then 30. Demagnetizing won't help here, and regulating the watch to compensate only papers over rising friction. A service — cleaning, fresh lubricants, new gaskets — resets the movement to its proper rate and protects it from actual wear.
        </p>

        <h2>Position and lifestyle effects</h2>

        <p>
          A mechanical watch doesn't run at one rate. Gravity acts on the balance wheel's pivots differently depending on orientation, so the watch runs slightly differently dial-up, dial-down, crown-up, and so on. This is why COSC tests chronometers in five positions. On a healthy movement the spread between positions is small — a few seconds per day — but it's real, and it's why your watch can gain on the wrist all day and then lose a little overnight on the nightstand.
        </p>

        <p>
          Winding state matters too. A mainspring near full wind drives the balance at higher amplitude than one that's nearly run down, and the rate changes with amplitude — an effect watchmakers call isochronism error. An automatic watch worn through an active day stays near full wind; the same watch on a sedentary day runs at lower amplitude and may keep slightly different time.
        </p>

        <p>
          You can even use this deliberately. If your watch runs a touch fast, try resting it crown-down or crown-up overnight and measure the difference — many owners find a storage position that trims a few seconds off the daily rate for free.
        </p>

        <h2>When it's normal variation, not a problem</h2>

        <p>
          Before assuming something is wrong, know what "right" looks like. Many non-chronometer mechanical movements are within the manufacturer's spec anywhere from roughly ±10 to ±20 seconds per day, and COSC-certified chronometers are allowed −4 to +6 seconds per day on average. Day-to-day scatter of a few seconds around a stable average is the nature of a mechanical movement, not a defect. Our guide to <Link to="/accuracy">how accurate a mechanical watch should be</Link> covers the standards and typical ranges in detail.
        </p>

        <p>
          The red flags are changes in the pattern, not the existence of one: a sudden jump (think magnetism), a steady march in one direction (think service), or a spread between positions that keeps widening.
        </p>

        <h2>How to tell which case is yours</h2>

        <p>
          One reading tells you almost nothing — the diagnosis is in the trend. Check the watch against a reliable reference at the same time each day for several days, note the daily gain or loss, and note how the watch rested overnight. A week of data separates the cases cleanly: a large constant gain points to magnetism (confirm with the compass test), a slow consistent loss on an aging movement points to service, and scatter that correlates with storage position is just the movement being mechanical.
        </p>

        <p>
          You can do this with pen and paper against an atomic time source — our guide to <Link to="/measure-watch-accuracy">measuring watch accuracy at home</Link> walks through the method and its pitfalls. An app like ChronoLog automates it: it timestamps each check against atomic time, computes the daily rate in seconds per day, and keeps the history per watch, and its <Link to="/timegrapher">audio timegrapher</Link> can read rate and beat error through the phone microphone (typically within ±2–5 s/day) for an instant snapshot. Either way, measure first — a few days of real numbers will tell you whether you need a $15 demagnetizer, a service, or nothing at all.
        </p>

        <div className="article-cta">
          <h2>Know your watch's real rate</h2>
          <p>
            Track any watch against atomic time, spot sudden rate changes early, and check rate and beat error with the audio timegrapher — no extra hardware. $5.99 one-time on the <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">App Store</a>.
          </p>
        </div>

        <div className="related-guides">
          <h2>Related guides</h2>
          <ul>
            <li><Link to="/accuracy">How accurate should a mechanical watch be?</Link></li>
            <li><Link to="/measure-watch-accuracy">How to measure watch accuracy at home</Link></li>
            <li><Link to="/what-is-a-timegrapher">What is a timegrapher? Readings explained</Link></li>
            <li><Link to="/brand-accuracy">Watch accuracy by brand: Rolex, Omega, Seiko</Link></li>
          </ul>
        </div>
      </article>
    </div>
  );
}

export default WatchRunningFastPage;
